import { CONFIRM_PHRASE_GO_LIVE, CONFIRM_PHRASE_PANIC, type EngineStateResponse, type PanicResult } from '@et/shared';
import { useState } from 'react';
import { api, describeError } from '../api/client';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useLive } from '../stores/live';
import { ConfirmDialog } from './Dialog';

/** Mode badge, engine state chip, pause/resume, circuit-breaker reset and the panic button. */

const STATE_TONE: Record<string, string> = { running: 'good', paused: 'warning', halted: 'serious', panic: 'critical', starting: '' };

export function ModeBadge(): JSX.Element | null {
  const status = useLive((s) => s.status);
  const tr = useT();
  if (!status) return null;
  return status.mode === 'live' ? <span className="chip live">{tr('LIVE — real money')}</span> : <span className="chip">{tr('PAPER')}</span>;
}

export function EngineChip(): JSX.Element | null {
  const status = useLive((s) => s.status);
  const tr = useT();
  if (!status) return null;
  return (
    <span className={`chip ${STATE_TONE[status.state] ?? ''}`}>
      <span className="dot" /> {tr('Engine')} {tr(status.state)}
    </span>
  );
}

export function WsChip(): JSX.Element {
  const ws = useLive((s) => s.ws);
  const tr = useT();
  return <span className={`chip ${ws === 'open' ? 'good' : 'warning'}`}><span className="dot" /> {ws === 'open' ? tr('connected') : tr(ws)}</span>;
}

export function EngineControls(): JSX.Element | null {
  const status = useLive((s) => s.status);
  const [busy, setBusy] = useState(false);
  const [panicStep, setPanicStep] = useState<0 | 1 | 2>(0);
  const [liveStep, setLiveStep] = useState<0 | 1 | 2 | 3>(0);
  const tr = useT();
  if (!status) return null;

  const run = async (fn: () => Promise<unknown>, ok: string): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      await useLive.getState().loadAll();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const panic = async (): Promise<void> => {
    setBusy(true);
    try {
      const r = await api.post<PanicResult>('/engine/panic', { confirmPhrase: CONFIRM_PHRASE_PANIC });
      const failed = r.results.filter((x) => x.status === 'failed');
      if (failed.length > 0) toast.error(t('Panic: {n} position(s) not closed — check Positions', { n: failed.length }));
      else toast.success(t('Panic complete — {n} position(s) closed, engine {state}', { n: r.results.length, state: t(r.state) }));
      await useLive.getState().loadAll();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
      setPanicStep(0);
    }
  };

  const switchMode = async (mode: 'live' | 'paper'): Promise<void> => {
    setBusy(true);
    try {
      await api.post('/engine/mode', mode === 'live' ? { mode, confirmPhrase: CONFIRM_PHRASE_GO_LIVE } : { mode });
      toast.success(mode === 'live' ? t('LIVE mode on — the engine is paused; resume when ready') : t('Back to paper mode'));
      await useLive.getState().loadAll();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
      setLiveStep(0);
    }
  };

  return (
    <div className="row">
      {status.mode === 'paper' ? (
        <button disabled={busy} onClick={() => { setLiveStep(1); }}>{tr('Go live…')}</button>
      ) : (
        <button disabled={busy} onClick={() => { setLiveStep(3); }}>{tr('Back to paper')}</button>
      )}
      {status.state === 'running' ? (
        <button disabled={busy} onClick={() => void run(() => api.post<EngineStateResponse>('/engine/pause'), t('Engine paused — exits keep running'))}>{tr('Pause')}</button>
      ) : (
        <button className="primary" disabled={busy || status.state === 'panic'} onClick={() => void run(() => api.post<EngineStateResponse>('/engine/resume'), t('Engine running'))}>{tr('Resume')}</button>
      )}
      {status.circuitBreaker.tripped && (
        <button disabled={busy} onClick={() => void run(() => api.post('/engine/circuit-breaker/reset'), t('Circuit breaker reset'))}>{tr('Reset breaker')}</button>
      )}
      <button className="danger" disabled={busy} onClick={() => { setPanicStep(1); }}>{tr('PANIC')}</button>

      {liveStep === 1 && (
        <ConfirmDialog title={tr('Switch to LIVE trading?')} confirmLabel={tr('Continue')} danger onCancel={() => { setLiveStep(0); }} onConfirm={() => { setLiveStep(2); }}>
          <p>{tr('From now on orders use real money on Binance. Before continuing:')}</p>
          <ul>
            <li>{tr('Binance key: spot trading only, withdrawals disabled, IP-restricted — and verified on the Credentials page')}</li>
            <li>{tr('The engine is paused and no paper positions are open')}</li>
            <li>{tr('Risk limits (position size, daily drawdown) are set deliberately; start with a small balance')}</li>
          </ul>
        </ConfirmDialog>
      )}
      {liveStep === 2 && (
        <ConfirmDialog title={tr('Final confirmation')} confirmLabel={tr('Go live')} danger phrase={CONFIRM_PHRASE_GO_LIVE} busy={busy} onCancel={() => { setLiveStep(0); }} onConfirm={() => void switchMode('live')}>
          {tr('The engine stays paused after the switch; nothing trades until you resume it.')}
        </ConfirmDialog>
      )}
      {liveStep === 3 && (
        <ConfirmDialog title={tr('Back to paper mode?')} confirmLabel={tr('Switch to paper')} busy={busy} onCancel={() => { setLiveStep(0); }} onConfirm={() => void switchMode('paper')}>
          {tr('Pause the engine and close live positions first; the switch is refused otherwise.')}
        </ConfirmDialog>
      )}
      {panicStep === 1 && (
        <ConfirmDialog title={tr('Liquidate everything?')} confirmLabel={tr('Continue')} danger onCancel={() => { setPanicStep(0); }} onConfirm={() => { setPanicStep(2); }}>
          {tr('Every open position ({n}) is market-sold to USDT and the engine halts. It will not resume on its own.', { n: status.openPositions })}
        </ConfirmDialog>
      )}
      {panicStep === 2 && (
        <ConfirmDialog title={tr('Final confirmation')} confirmLabel={tr('Liquidate now')} danger phrase={CONFIRM_PHRASE_PANIC} busy={busy} onCancel={() => { setPanicStep(0); }} onConfirm={() => void panic()}>
          {tr('This cannot be undone.')}
        </ConfirmDialog>
      )}
    </div>
  );
}
